import { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotificationStore } from '../../store/notificationStore';
import { cn } from '../../utils/cn';

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const items = useNotificationStore((s) => s.items);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const markAllRead = useNotificationStore((s) => s.markAllRead);
  const navigate = useNavigate();

  useEffect(() => {
    const onDoc = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const toggle = () => {
    setOpen((v) => !v);
    markAllRead();
  };

  const handleItemClick = (n) => {
    setOpen(false);
    if (n.meta?.driverId) {
      navigate(`/drivers/${n.meta.driverId}`);
    } else if (n.meta?.vehicleId) {
      navigate(`/vehicles/${n.meta.vehicleId}`);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={toggle}
        className={cn(
          'relative p-2 rounded-lg transition-all duration-200',
          'hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark',
          'text-text-secondary dark:text-text-secondary-dark hover:text-text dark:hover:text-text-dark',
          'hover:scale-110 active:scale-95'
        )}
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[8px] h-2 px-0.5 bg-danger rounded-full ring-2 ring-surface dark:ring-surface-dark" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-surface dark:bg-surface-dark border border-border dark:border-border-dark rounded-xl shadow-xl z-50">
          <div className="p-3 border-b border-border dark:border-border-dark text-sm font-semibold text-text dark:text-text-dark">
            Notifications
          </div>
          {items.length === 0 ? (
            <div className="p-4 text-sm text-text-secondary dark:text-text-secondary-dark">No notifications yet</div>
          ) : (
            <ul className="divide-y divide-border dark:divide-border-dark">
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => handleItemClick(n)}
                    className="w-full text-left p-3 hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark transition-colors"
                  >
                    <p className="text-sm font-medium text-text dark:text-text-dark">{n.title}</p>
                    <p className="text-xs text-text-secondary dark:text-text-secondary-dark mt-0.5 line-clamp-3">
                      {n.message}
                    </p>
                    <p className="text-[10px] text-text-tertiary dark:text-text-tertiary-dark mt-1">
                      {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
